/**
 * CAP-7 ordered-sequence primitive — generic contract for parse / step /
 * format / anchors. Academic terms are one registered instance; other
 * sequences (cohorts, fiscal periods, …) plug into the same registry.
 */

export interface AnchorMap {
  readonly self: string;
  readonly prior: string;
  readonly priorPrior: string;
}

export interface OrderedSequence<TMember> {
  readonly id: string;
  parse(input: string): TMember | null;
  step(member: TMember, n: number): TMember | null;
  format(member: TMember): string;
  anchors(current: string): AnchorMap | null;
}

/**
 * Shared three-point anchor derivation for sequences where `step(member, -1)`
 * walks backward one position at a time.
 */
export function buildThreePointAnchors<TMember>(
  sequence: Pick<OrderedSequence<TMember>, 'parse' | 'step' | 'format'>,
  current: string,
): AnchorMap | null {
  const selfMember = sequence.parse(current);
  if (selfMember === null) {
    return null;
  }

  const priorMember = sequence.step(selfMember, -1);
  if (priorMember === null) {
    return null;
  }

  const priorPriorMember = sequence.step(priorMember, -1);
  if (priorPriorMember === null) {
    return null;
  }

  return {
    self: sequence.format(selfMember),
    prior: sequence.format(priorMember),
    priorPrior: sequence.format(priorPriorMember),
  };
}
